import { Agent, request, errors as undiciErrors, type Dispatcher } from "undici";
import type { HttpClient, HttpRequestOptions, HttpResponse } from "./types.js";
import { CancelledError, HttpError, TimeoutError } from "../core/errors.js";
import { unsafeBrandUrl, type Url } from "../data-model/primitives.js";
import type { Logger } from "../utils/logger.js";

const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_REDIRECTS = 5;
const REDIRECT_STATUSES: ReadonlySet<number> = new Set([301, 302, 303, 307, 308]);

/**
 * Leaf HTTP client. Dispatches through a dispatcher it controls — never the
 * process-global one. undici copies in a process share the global dispatcher
 * via a `Symbol.for(...)` registry, so a v7 copy claimed by an unrelated
 * dependency would otherwise reject this v6 client's `maxRedirections` option.
 */
export class UndiciHttpClient implements HttpClient {
  readonly #logger: Logger;
  readonly #dispatcher: Dispatcher;
  readonly #ownsDispatcher: boolean;

  constructor(opts: { logger: Logger; dispatcher?: Dispatcher }) {
    this.#logger = opts.logger;
    this.#ownsDispatcher = opts.dispatcher === undefined;
    this.#dispatcher = opts.dispatcher ?? new Agent();
  }

  async get(url: Url, opts?: HttpRequestOptions): Promise<HttpResponse> {
    const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const half = Math.max(1, Math.floor(timeoutMs / 2));
    let res: Awaited<ReturnType<typeof request>> | undefined;
    try {
      // Follow redirects manually rather than via `maxRedirections`: that
      // option was removed in undici v7, and a manual loop lets us report the
      // true post-redirect URL on `HttpResponse.url`.
      let currentUrl = url;
      for (let hop = 0; ; hop++) {
        res = await request(currentUrl, {
          method: "GET",
          dispatcher: this.#dispatcher,
          ...(opts?.headers !== undefined ? { headers: opts.headers } : {}),
          ...(opts?.signal !== undefined ? { signal: opts.signal } : {}),
          headersTimeout: half,
          bodyTimeout: half,
        });
        const next = hop < MAX_REDIRECTS ? redirectTarget(res.statusCode, res.headers, currentUrl) : undefined;
        if (next === undefined) break;
        await res.body.dump();
        currentUrl = next;
      }
      const body = await res.body.text();
      const headers = normalizeHeaders(res.headers);
      return {
        status: res.statusCode,
        headers,
        body,
        url: currentUrl,
      };
    } catch (cause) {
      // Release the socket back to the pool promptly if the body was started but
      // not fully consumed (e.g. a mid-read failure). dump() is a no-op if already drained.
      if (res !== undefined) await res.body.dump().catch(() => {});
      if (isAbortError(cause, opts?.signal)) {
        throw new CancelledError({ cause });
      }
      if (
        cause instanceof undiciErrors.HeadersTimeoutError ||
        cause instanceof undiciErrors.BodyTimeoutError
      ) {
        throw new TimeoutError(url, { cause });
      }
      const detail = cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause);
      this.#logger.warn({ url, cause }, "undici network error");
      throw new HttpError(0, url, `Network error (${detail})`, { cause });
    }
  }

  /** Closes the dispatcher this client created. A no-op when one was injected — the caller owns that. */
  async dispose(): Promise<void> {
    if (this.#ownsDispatcher) await this.#dispatcher.close();
  }
}

/**
 * Resolves the next URL to fetch for a redirect response, or `undefined` when
 * the response is not a followable redirect (non-3xx, missing/unparseable
 * `Location`). A relative `Location` is resolved against the current URL.
 */
function redirectTarget(
  status: number,
  headers: Record<string, string | string[] | undefined>,
  currentUrl: Url,
): Url | undefined {
  if (!REDIRECT_STATUSES.has(status)) return undefined;
  const raw = headers["location"];
  const location = Array.isArray(raw) ? raw[0] : raw;
  if (location === undefined || location === "") return undefined;
  try {
    return unsafeBrandUrl(new URL(location, currentUrl).href);
  } catch {
    return undefined;
  }
}

function normalizeHeaders(
  h: Record<string, string | string[] | undefined>,
): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(h)) {
    if (v == null) continue;
    out[k.toLowerCase()] = Array.isArray(v) ? v.join(", ") : String(v);
  }
  return out;
}

function isAbortError(err: unknown, signal: AbortSignal | undefined): boolean {
  if (signal?.aborted) return true;
  if (err && typeof err === "object" && "name" in err) {
    return err.name === "AbortError";
  }
  return false;
}
